import React from 'react';
import { ArrowRight } from 'lucide-react';
import SlimGridMotion from '../components/SlimGridMotion';
import OptionsAccordion from '../components/services/OptionsAccordion';
import UspStrip from '../components/services/UspStrip';
import Reveal from '../components/home/Reveal';
import GreenButton from '../components/home/GreenButton';
import SectionHeading from '../components/home/SectionHeading';
import HowItWorks from '../components/home/HowItWorks';
import { useContactModal } from '../contexts/ContactModalContext';
import type { ServiceOption } from '../data/services/types';

const gridImages = [
  '/home/services/signboards.jpg',
  '/pavement-signs/pavement1.jpg',
  '/projecting-signs/projecting1.jpg',
  '/home/services/window-film.jpg',
  '/signboards/signboard1.jpg',
  '/home/signage/projecting-kearns.jpg',
  '/pavement-signs/pavement2.jpg',
  '/home/signage/signboard-kearns.jpg',
  '/projecting-signs/projecting2.jpg',
  '/home/signage/glass-manifestation.jpg',
  '/signboards/signboard2.jpg',
  '/home/services/pavement.jpg',
  '/home/signage/signboard-ijr.jpg',
  '/projecting-signs/projecting3.jpg',
  '/pavement-signs/pavement3.jpg',
  '/home/signage/etch-hodghill.jpg',
  '/signboards/signboard3.jpg',
  '/home/services/projecting.jpg',
  '/home/signage/signboard-kemp.jpg',
  '/pavement-signs/pavement4.jpg',
  '/signboards/signboard4.jpg',
];

const signageOptions: ServiceOption[] = [
  {
    id: 'walls',
    title: 'Walls',
    lead: 'Custom fabricated signs, flat-cut lettering and composite panels.',
    body: 'Find the signage style that works best for your walls – available in a range of materials for internal and exterior use, from flat-cut lettering on composite panels to built-up illuminated 3D letters.',
    image: '/home/services/signboards.jpg',
    buttons: [
      { label: 'Signboards', to: '/services/signboards' },
      { label: 'Raised Lettering', to: '/services/raised-lettering' },
    ],
  },
  {
    id: 'projecting-signs',
    title: 'Projecting signs',
    lead: 'Wall-mounted brackets and customisable double-sided panels.',
    body: 'An excellent way to create signage that can be seen from all angles and improve your high-street visibility. Double-sided, so passing trade catches you from both directions.',
    image: '/home/services/projecting.jpg',
    buttons: [
      { label: 'Projecting Signs', to: '/services/projecting-signs' },
    ],
  },
  {
    id: 'pavement-signs',
    title: 'Pavement signs',
    lead: 'Catch the attention of passers-by and increase footfall.',
    body: 'Free-standing A-boards, swing signs and water-base signs in a range of styles, colours and sizes suitable for different weather conditions. Printed panels or chalkboard inserts.',
    image: '/home/services/pavement.jpg',
    buttons: [
      { label: 'Pavement Signs', to: '/services/pavement-signs' },
    ],
  },
  {
    id: 'windows',
    title: 'Windows',
    lead: 'Add privacy, branding and promotion to your glazing.',
    body: 'The etched finish gives a sandblasted, frosted look to glass windows and doors for privacy, while full-colour window graphics and one-way vision film transform your glass into high-impact advertising.',
    image: '/home/services/window-film.jpg',
    buttons: [
      { label: 'Window Privacy Film', to: '/services/window-privacy-film' },
      { label: 'Window Graphics', to: '/services/window-graphics' },
    ],
  },
  {
    id: 'glass-manifestation',
    title: 'Glass manifestation',
    lead: 'Make it visible and compliant with building regulations.',
    body: 'Large areas of clear glass need visible markings so people do not walk into them. We etch or print manifestation to match your brand, so it looks like part of the design rather than an afterthought.',
    image: '/home/signage/glass-manifestation.jpg',
    buttons: [
      { label: 'Glass Manifestation', to: '/services/glass-manifestation' },
    ],
  },
  {
    id: 'banners',
    title: 'Banners',
    lead: 'Heavyweight printed PVC and perimeter site hoarding banners.',
    body: 'Hemmed, eyeleted and ready to hang. Heavy-duty banners for outdoor promotions and events, plus mesh and PVC banners sized to fit standard Heras fence panels.',
    image: '/home/signboards/signboards-banners.jpg',
    buttons: [
      { label: 'PVC Banners', to: '/services/pvc-banners' },
      { label: 'Heras Fencing Banners', to: '/services/heras-fencing-banners' },
    ],
  },
];

const steps = [
  {
    title: 'Get a free quote',
    text: 'Call, email or message us with what you need. We will talk through options and materials that suit your budget.',
  },
  {
    title: 'Design and survey',
    text: 'Our design team puts together a visual of how your signage will look. We measure up on site where it matters.',
  },
  {
    title: 'Approve your order',
    text: 'Sign off the mockup and we go into production in our Kidderminster unit. Most jobs complete within 5 to 10 working days.',
  },
  {
    title: 'Install',
    text: 'Our insured team fits everything from a single A-board to a full shopfront, or collect and fit it yourself.',
  },
];

const signageFaqs = [
  {
    q: 'Can you help with artwork and design?',
    a: 'Yes. Our in-house design team puts together a visual of how your signage will look, with mockups and visuals at no extra cost.',
  },
  {
    q: 'Do you carry out site surveys and installation?',
    a: 'Yes. Our insured team fits everything from a single A-board to a full shopfront, or you can collect and fit it yourself.',
  },
];

const AllSignage: React.FC = () => {
  const { openContactModal } = useContactModal();

  const scrollToTypes = () => {
    document.querySelector('#what-we-make')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-white text-black">
      {/* Hero: grid motion background */}
      <section
        className="relative overflow-hidden bg-black text-white min-h-[70svh] lg:h-[calc(60svh_-_var(--header-h,0px))] lg:min-h-[540px] flex items-center"
      >
        <div className="absolute inset-0 isolate">
          <SlimGridMotion items={gridImages} gradientColor="transparent" />
        </div>
        <div className="absolute inset-0 z-[1] bg-gradient-to-r from-black/95 via-black/70 to-black/30" />
        <div className="absolute inset-0 z-[1] bg-gradient-to-t from-black/70 via-transparent to-black/40" />

        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 py-16">
          <div className="max-w-3xl">
            <div className="flex items-center gap-5 mb-5">
              <span className="h-px w-12 bg-[#78BE20]" />
              <span className="font-poppins text-[11px] md:text-xs font-semibold tracking-[0.25em] uppercase">
                Signage
              </span>
            </div>
            <h1 className="font-montserrat font-extrabold leading-[1.02] tracking-[-0.03em] text-[clamp(34px,4vw,60px)]">
              Communicate your message with{' '}
              <span className="text-[#78BE20]">custom signage.</span>
            </h1>
            <p className="font-poppins text-base md:text-lg leading-7 text-white/80 mt-6 max-w-xl">
              From off-the-shelf panels to fully custom fabricated signs made to your specifications. Our team will help find the best solution for your space, environment and budget.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <GreenButton onClick={scrollToTypes}>Explore Options</GreenButton>
              <GreenButton onClick={openContactModal} variant="outline" className="text-white">
                Get in Touch
              </GreenButton>
            </div>
          </div>
        </div>
      </section>

      {/* Service options */}
      <section id="what-we-make" className="py-16 md:py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <Reveal className="mb-12 lg:mb-16">
            <SectionHeading title="What do we offer" tone="green" underline />
          </Reveal>
          <OptionsAccordion items={signageOptions} />
        </div>
      </section>

      {/* Green USP Bar */}
      <UspStrip
        items={[
          'MADE TO ORDER',
          'EXPERIENCED INSTALLATION TEAM',
          'FULLY INSURED',
          'IN-HOUSE DESIGN',
          'TRUSTED MATERIALS',
        ]}
      />

      {/* Process on black */}
      <HowItWorks steps={steps} tone="dark" />

      {/* Questions, answered on black */}
      <section className="py-16 md:py-20 px-6 bg-black text-white border-t border-white/10">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-10 lg:gap-20 items-start">
          <Reveal>
            <h2 className="font-montserrat font-extrabold tracking-[-0.03em] leading-[1.05] text-[clamp(30px,3.4vw,44px)]">
              Questions, answered
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <div className="divide-y divide-white/10 border-y border-white/10">
              {signageFaqs.map((f) => (
                <details key={f.q} className="group">
                  <summary className="flex items-center justify-between gap-6 py-5 cursor-pointer list-none font-montserrat font-bold text-base md:text-lg [&::-webkit-details-marker]:hidden">
                    {f.q}
                    <span className="shrink-0 w-5 h-5 text-white/40 transition-transform duration-500 group-open:rotate-45 group-open:text-[#78BE20]">
                      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M10 4v12M4 10h12" />
                      </svg>
                    </span>
                  </summary>
                  <div className="pb-6">
                    <p className="font-poppins text-sm md:text-base leading-7 text-white/70 max-w-xl">{f.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Green CTA band */}
      <section className="w-full py-20 md:py-28 px-6 bg-[#78BE20] text-white">
        <div className="max-w-[900px] mx-auto text-center">
          <Reveal>
            <h2 className="font-montserrat font-extrabold tracking-[-0.03em] leading-[1.05] text-[clamp(36px,5vw,64px)] mb-4">
              Ready to get started?
            </h2>
            <p className="font-poppins text-white/85 text-base md:text-lg mb-10 max-w-xl mx-auto">
              Free quote and design mockup for any of our signage services.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                type="button"
                onClick={openContactModal}
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-black font-poppins font-semibold uppercase tracking-[0.12em] text-[11px] hover:bg-black hover:text-white transition-colors"
              >
                Get in touch
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </button>
              <a
                href="tel:01562227117"
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-white text-white font-poppins font-semibold uppercase tracking-[0.12em] text-[11px] hover:bg-white hover:text-black transition-colors"
              >
                Call 01562 227 117
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default AllSignage;
